/* eslint-disable functional/no-expression-statement,functional/immutable-data */
import * as D3Ease from "d3-ease";
import * as D3Scale from "d3-scale";
import * as D3Selection from "d3-selection";
import * as D3Shape from "d3-shape";

import { pipe } from "fp";
import moment from "moment-timezone";

import * as Color from "~/Color";
import * as Driver from "~/Driver";
import * as Log from "~/Log";
import { Time } from "~/Time";

import * as ChartIcon from "./Icon";

const toDuration = (d: Log.Chart.LogChartData): string =>
  d.stop.valueOf() - d.start.valueOf() > 0
    ? Time.Duration.toEnglish({
        duration: d.stop.valueOf() - d.start.valueOf(),
      })
    : "";

const timezoneDelta = (
  value: number | undefined,
  timezone: Time.Zone
): number => {
  const localZoneOffset = moment(value).tz(moment.tz.guess()).utcOffset();
  const currentZoneOffset = moment(value).tz(timezone.zone).utcOffset();
  return (localZoneOffset - currentZoneOffset) * -1;
};

export const getXScaledValue = (
  value: number | undefined,
  xScale: D3Scale.ScaleTime<number, number, never>,
  timezone: Time.Zone
): number => {
  const offsetDelta = timezoneDelta(value, timezone);
  return xScale(value ? value.valueOf() + offsetDelta * 1000 * 60 : 0) ?? 0;
};

export const isDefined = (
  data: Log.Chart.LogChartData | undefined
): boolean => {
  return (data && data?.start && !isNaN(data.start.valueOf())) || false;
};

const lineGenerator = (
  xScale: D3Scale.ScaleTime<number, number, never>,
  yScale: Log.Chart.YScale,
  timezone: Time.Zone
) =>
  D3Shape.line<Log.Chart.LogChartData>()
    .defined((d) => isDefined(d))
    .x((d) => getXScaledValue(d?.start, xScale, timezone))
    .y((d) => yScale(d.status) ?? 0);

export const generate = ({
  svg,
  mergedStatusEvents,
  xScale,
  yScale,
  topMargin,
  defaultTimezone,
}: Log.Chart.ChartGeneratorProps): void => {
  const pathGroup = svg
    .append("g")
    .attr("class", "status-path-group")
    .attr("transform", `translate(0,${topMargin - 2})`);

  const line = pathGroup
    .append("path")
    .datum(mergedStatusEvents)
    .attr("class", "status-path")
    .attr("fill", "none")
    .attr("stroke", Color.azure())
    .attr("stroke-width", 1.5)
    .attr("d", lineGenerator(xScale, yScale, defaultTimezone));

  const totalLength = line.node()?.getTotalLength();
  totalLength &&
    line
      .attr("stroke-dasharray", `${totalLength} ${totalLength}`)
      .attr("stroke-dashoffset", totalLength)
      .transition()
      .duration(1200)
      .ease(D3Ease.easeLinear)
      .attr("stroke-dashoffset", 0);

  const eventTip = D3Selection.select(".event-tip");
  const tipTitle = D3Selection.select(".tip-title");
  const tipContent = D3Selection.select(".tip-content");

  const statusSpecificDescription = (d: Log.Chart.LogChartData): string =>
    d.status === "D" ? ` for <strong>${d.event.distance}mi</strong>` : "";

  pathGroup
    .selectAll(".dot")
    .data(mergedStatusEvents)
    .enter()
    .append("circle")
    .attr("class", "dot")
    .attr("fill", Color.azure())
    .attr("r", 6)
    .attr("cx", (d: Log.Chart.LogChartData) =>
      getXScaledValue(d.start, xScale, defaultTimezone)
    )
    .attr("cy", (d: Log.Chart.LogChartData) => yScale(d.status) ?? 0)
    .on("mouseover", (event: MouseEvent, d) => {
      eventTip.transition().duration(200).style("opacity", 1);
      tipTitle.html(
        `<span><strong>${Time.DateTime.toHMMSS(
          {
            dateTime: d.start.valueOf(),
          },
          defaultTimezone
        )}</strong> ${toDuration(d)}</span>`
      );
      tipContent.html(
        `<div>${ChartIcon.toStatusSVG(d)}&nbsp;<strong>${
          d.event.status
        }</strong>${statusSpecificDescription(
          d
        )} via <strong>${Log.Event.Source.toDescription(
          d.event.sources.current
        )}</strong> at <strong>${d.event.location}</strong></div>`
      );
      eventTip
        .style("left", `${event.pageX + 12}px`)
        .style("top", `${event.pageY - 8}px`);
    })
    .on("mouseout", () => {
      eventTip.transition().duration(500).style("opacity", 0);
    });
};

export const mapData = (
  statusEvents: readonly Log.Event.Event[],
  localDayStart: number,
  driverTimezone: Time.Zone,
  zoneDelta = 12
): readonly Log.Chart.LogChartData[] => {
  const { start: dataStart, stop: dataStop } = pipe(
    Time.DateTime.toDayInterval({ dateTime: localDayStart }, driverTimezone),
    Time.Interval.Stopped.expandLeft(Time.Duration.hours(zoneDelta)),
    Time.Interval.Stopped.expandRight(Time.Duration.hours(zoneDelta))
  ).interval;

  return pipe(
    statusEvents,
    (events) =>
      events.map((event) => ({
        status: Driver.Status.Actual.fromStatus(event.status),
        start: event.interval.start.dateTime,
        stop: event.interval.stop.dateTime,
        source:
          event.sources.current.type || Log.Event.Source.Type.application(),
        event,
      })),
    (data) =>
      // eslint-disable-next-line functional/prefer-readonly-type
      data.reduce<Log.Chart.LogChartData[]>(
        (previousValue, currentValue, index, array) => {
          if (index < array.length - 1) {
            const start =
              index === 0 &&
              moment(currentValue.start).isBefore(moment(dataStart.dateTime))
                ? moment(dataStart.dateTime).valueOf()
                : currentValue.start;
            const nexValue: Log.Chart.LogChartData = data[index + 1];

            previousValue.push({
              status: currentValue.status,
              start: start,
              stop: nexValue.start,
              source: currentValue.source,
              event: currentValue.event,
            });

            previousValue.push({
              status: currentValue.status,
              start: nexValue.start,
              stop: nexValue.stop,
              source: currentValue.source,
              event: currentValue.event,
            });
          } else {
            const endingTime = moment(currentValue.stop).isAfter(
              dataStop.dateTime
            )
              ? moment(dataStop.dateTime).valueOf()
              : currentValue.stop;

            previousValue.push({
              status: currentValue.status,
              start: currentValue.start,
              stop: endingTime,
              source: currentValue.source,
              event: currentValue.event,
            });

            previousValue.push({
              status: currentValue.status,
              start: endingTime,
              stop: endingTime,
              source: currentValue.source,
              event: currentValue.event,
            });
          }
          return previousValue;
        },
        []
      )
  );
};

export const update = ({
  svg,
  xScale,
  yScale,
  mergedStatusEvents,
  defaultTimezone,
}: Log.Chart.ChartGeneratorProps): void => {
  svg
    .select(".status-path")
    .datum(mergedStatusEvents)
    .attr("d", lineGenerator(xScale, yScale, defaultTimezone));

  svg
    .selectAll(".dot")
    .data(mergedStatusEvents)
    .attr("cx", (d) => getXScaledValue(d.start, xScale, defaultTimezone))
    .attr("cy", (d) => yScale(d.status) ?? 0);
};
